import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  PortalDsButton,
  PortalDsField,
  PortalDsInput,
  portalDs,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface CampaignEmailTestSendModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scheduleName?: string;
  defaultEmail?: string;
  isSending?: boolean;
  error?: string | null;
  successMessage?: string | null;
  onSend: (email: string) => void | Promise<void>;
}

export function CampaignEmailTestSendModal({
  open,
  onOpenChange,
  scheduleName,
  defaultEmail = '',
  isSending = false,
  error = null,
  successMessage = null,
  onSend,
}: CampaignEmailTestSendModalProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail(defaultEmail);
      setValidationError(null);
    }
  }, [defaultEmail, open]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setValidationError('Enter an email address.');
      return;
    }
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setValidationError('Enter a valid email address.');
      return;
    }
    setValidationError(null);
    void onSend(trimmedEmail);
  };

  const displayError = validationError ?? error;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="bg-white sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              Send test email
            </DialogTitle>
            <DialogDescription>
              {scheduleName
                ? `Send "${scheduleName}" to a single address. This does not email configured recipients or update the schedule.`
                : 'Send this campaign performance email to a single test address.'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {successMessage ? (
              <p
                className={cn(portalDs.bodySm, 'text-[#15803D]')}
                role="status"
              >
                {successMessage}
              </p>
            ) : (
              <PortalDsField
                label="Test email address"
                required
                error={displayError ?? undefined}
              >
                <PortalDsInput
                  type="email"
                  value={email}
                  disabled={isSending}
                  placeholder="you@example.com"
                  autoComplete="email"
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setValidationError(null);
                  }}
                />
              </PortalDsField>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <PortalDsButton
              type="button"
              variant="ghost"
              disabled={isSending}
              onClick={() => onOpenChange(false)}
            >
              {successMessage ? 'Close' : 'Cancel'}
            </PortalDsButton>
            {!successMessage ? (
              <PortalDsButton
                type="submit"
                variant="primary"
                disabled={isSending}
              >
                {isSending ? (
                  <>
                    <Loader2
                      className="size-4 animate-spin"
                      aria-hidden
                    />
                    Sending…
                  </>
                ) : 'Send test email'}
              </PortalDsButton>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
